import type { Activity } from '@/constants/crm'
import type { ActivityGroup } from '@/lib/crm'

import { useMemo, useState } from 'react'
import Avatar from '@/components/ui/avatar'
import Button from '@/components/ui/button'
import CountBadge from '@/components/ui/count-badge'
import FilterMenu from '@/components/generals/filter-menu'
import CompanyMark from '@/components/generals/company-mark'
import ActivityIcon from '@/components/activities/activity-icon'
import { Checkbox } from '@/components/ui/checkbox'
import { EmptyState, ViewToolbar } from '@/components/generals/view-parts'
import { OWNERS, ownerByName } from '@/constants/companies'
import { ACTIVITY_TYPES } from '@/constants/crm'
import { apiRequest } from '@/lib/client-api'
import { activityGroup, indexById } from '@/lib/crm'
import { formatShortDate } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { useCrmStore } from '@/stores/crm-store-provider'
import PlusIcon from '@/assets/images/_common/plus.svg'

const ALL = 'all'

const TYPE_TABS = [
    { value: ALL, label: "All" },
    ...ACTIVITY_TYPES.map(type => ({ value: type, label: `${type}s` }))
]

const STATUS_OPTIONS = [
    { value: 'open', label: "Open" },
    { value: 'completed', label: "Completed" },
    { value: ALL, label: "All" }
]

const OWNER_OPTIONS = [
    { value: ALL, label: "All Owners" },
    ...OWNERS.map(owner => ({ value: owner.name, label: owner.name }))
]

const GROUPS: Array<{ key: ActivityGroup, label: string }> = [
    { key: 'overdue', label: "Overdue" },
    { key: 'today', label: "Today" },
    { key: 'upcoming', label: "Upcoming" },
    { key: 'completed', label: "Completed" }
]

export default function ActivitiesView() {
    const today = useCrmStore(state => state.today)
    const status = useCrmStore(state => state.status)
    const activities = useCrmStore(state => state.activities)
    const companies = useCrmStore(state => state.companies)
    const contacts = useCrmStore(state => state.contacts)
    const upsertActivity = useCrmStore(state => state.upsertActivity)
    const openDialog = useCrmStore(state => state.openDialog)
    const openDetail = useCrmStore(state => state.openDetail)
    const openContact = useCrmStore(state => state.openContact)
    const showNotice = useCrmStore(state => state.showNotice)
    const [type, setType] = useState(ALL)
    const [statusFilter, setStatusFilter] = useState('open')
    const [owner, setOwner] = useState(ALL)

    const companyIndex = useMemo(() => indexById(companies), [companies])
    const contactIndex = useMemo(() => indexById(contacts), [contacts])
    const visible = activities.filter(activity => {
        if (type !== ALL && activity.type !== type) return false
        if (owner !== ALL && activity.owner !== owner) return false
        if (statusFilter === 'open' && activity.completed) return false
        if (statusFilter === 'completed' && !activity.completed) return false

        return true
    })

    const grouped = GROUPS
        .map(group => ({
            ...group,
            items: visible
                .filter(activity => activityGroup(activity, today) === group.key)
                .sort((a, b) => group.key === 'completed' ? b.dueDate.localeCompare(a.dueDate) : a.dueDate.localeCompare(b.dueDate))
        }))
        .filter(group => group.items.length > 0)

    async function toggle(activity: Activity) {
        const next = { ...activity, completed: !activity.completed }

        upsertActivity(next)

        const result = await apiRequest<{ activity: Activity }>(`/api/activities/${activity.id}`, 'PATCH', { completed: next.completed })

        if (!result.ok) {
            upsertActivity(activity)
            showNotice('error', result.message)

            return
        }

        upsertActivity(result.data.activity)
        showNotice('success', next.completed ? "Marked as done." : "Moved back to open.")
    }

    return (
        <>
            <div className='border-border flex shrink-0 items-center gap-4 overflow-x-auto border-b px-4' role='group' aria-label="Activity type">
                {TYPE_TABS.map(tab => {
                    const active = type === tab.value

                    return (
                        <button
                            key={tab.value}
                            type='button'
                            aria-pressed={active}
                            data-active={active}
                            onClick={() => setType(tab.value)}
                            className='group caption-style -mb-px grid cursor-pointer border-b border-transparent py-4 text-center whitespace-nowrap text-subtle outline-none select-none transition-[color,border-color] duration-150 ease-power3-out hover:text-soft focus-visible:text-foreground data-[active=true]:border-foreground data-[active=true]:text-foreground'
                        >
                            <span aria-hidden className='invisible col-start-1 row-start-1 font-medium'>
                                {tab.label}
                            </span>
                            <span className='col-start-1 row-start-1 group-data-[active=true]:font-medium'>
                                {tab.label}
                            </span>
                        </button>
                    )
                })}
            </div>

            <ViewToolbar>
                <div className='flex flex-wrap items-center gap-2'>
                    <FilterMenu label="Status" value={statusFilter} options={STATUS_OPTIONS} onChange={setStatusFilter} />
                    <FilterMenu label="Owner" value={owner} options={OWNER_OPTIONS} onChange={setOwner} />
                </div>
                <Button variant='primary' size='sm' onClick={() => openDialog('activity')}>
                    <PlusIcon aria-hidden className='size-3' />
                    Log Activity
                </Button>
            </ViewToolbar>

            <div className='border-border flex min-h-0 flex-1 flex-col overflow-y-auto border-t'>
                {grouped.length === 0 ? (
                    <EmptyState
                        title={status === 'loading' ? "Loading activities…" : "Nothing here"}
                        description={status === 'loading' ? undefined : "No activities match these filters. Log a call, email or meeting to keep accounts moving."}
                        action={status === 'loading' ? undefined : (
                            <Button variant='secondary' size='sm' onClick={() => openDialog('activity')}>
                                Log Activity
                            </Button>
                        )}
                    />
                ) : grouped.map(group => (
                    <section key={group.key} aria-label={group.label}>
                        <h2 className='caption-style border-border bg-background/95 sticky top-0 z-10 flex items-center gap-2 border-b px-4 py-2.5 font-medium backdrop-blur-sm'>
                            <span className={cn(group.key === 'overdue' ? 'text-danger' : 'text-soft')}>{group.label}</span>
                            <CountBadge>{group.items.length}</CountBadge>
                        </h2>
                        <ul>
                            {group.items.map(activity => {
                                const company = activity.companyId ? companyIndex.get(activity.companyId) : undefined
                                const contact = activity.contactId ? contactIndex.get(activity.contactId) : undefined
                                const activityOwner = ownerByName(activity.owner)
                                const overdue = group.key === 'overdue'

                                return (
                                    <li key={activity.id} className='border-border hover:bg-card/50 flex items-start gap-3 border-b px-4 py-3 transition-colors duration-150'>
                                        <Checkbox
                                            checked={activity.completed}
                                            onCheckedChange={() => toggle(activity)}
                                            aria-label={activity.completed ? `Mark ${activity.subject} as open` : `Mark ${activity.subject} as done`}
                                            className='mt-1.5'
                                        />
                                        <ActivityIcon type={activity.type} />
                                        <div className='flex min-w-0 flex-1 flex-col gap-2'>
                                            <span
                                                className={cn('lead-style block leading-[1.3]', activity.completed && 'text-subtle line-through decoration-white/20')}
                                                data-mdlfy-cms={`activities:${activity.id}:subject`}
                                            >
                                                {activity.subject}
                                            </span>
                                            {activity.notes && (
                                                <p className='caption-style text-subtle line-clamp-2 leading-[1.4]' data-mdlfy-cms={`activities:${activity.id}:notes`}>
                                                    {activity.notes}
                                                </p>
                                            )}
                                            <span className='caption-style text-soft flex flex-wrap items-center gap-x-3 gap-y-2'>
                                                {company && (
                                                    <button
                                                        type='button'
                                                        onClick={() => openDetail(company.id)}
                                                        className='hover:text-foreground flex cursor-pointer items-center gap-1.5 transition-colors duration-150'
                                                    >
                                                        <CompanyMark company={company} className='size-4 rounded-[4px]' />
                                                        <span data-mdlfy-cms={`companies:${company.id}:name`}>
                                                            {company.name}
                                                        </span>
                                                    </button>
                                                )}
                                                {contact && (
                                                    <button
                                                        type='button'
                                                        onClick={() => openContact(contact.id)}
                                                        className='hover:text-foreground cursor-pointer transition-colors duration-150'
                                                    >
                                                        <span data-mdlfy-cms={`contacts:${contact.id}:name`}>
                                                            {contact.name}
                                                        </span>
                                                    </button>
                                                )}
                                                <span className='flex items-center gap-1.5'>
                                                    <Avatar src={activityOwner.avatar} alt='' className='size-4' />
                                                    <span data-mdlfy-cms={`activities:${activity.id}:owner`}>
                                                        {activity.owner}
                                                    </span>
                                                </span>
                                            </span>
                                        </div>
                                        <span className={cn('caption-style shrink-0 pt-1 tabular-nums', overdue ? 'text-danger' : 'text-soft')}>
                                            {activity.dueDate === today ? "Today" : formatShortDate(activity.dueDate)}
                                        </span>
                                    </li>
                                )
                            })}
                        </ul>
                    </section>
                ))}
            </div>
        </>
    )
}
